// The product checkboxes' state transition, extracted from Partners.tsx so it
// can be tested without rendering the whole editor modal.
//
// It exists to keep ops away from two 422s that the API is right to return but
// that arrive too late to be useful: by the time `no_products` or
// `default_not_enabled` comes back, the form already looked fine and the admin
// has no way to tell which of the several fields they touched caused it. The
// API's resolveProductUpdate (apps/api/src/modules/partners/service.ts) stays
// the authority and the backstop — it validates the resulting ROW, including
// patches this UI never produces. This is the guard rail in front of it, not a
// second implementation of it.

import { isPartnerProduct, type PartnerProduct } from '@drivecars/shared';

export interface ProductState {
  enabled_products: string[];
  default_product: string | null;
}

export function nextProductState(
  current: ProductState,
  action: { toggle: PartnerProduct },
): ProductState {
  const enabled = current.enabled_products;
  const checked = enabled.includes(action.toggle);

  if (checked) {
    const next = enabled.filter((p) => p !== action.toggle);
    // Unchecking the last product is a no-op rather than an empty list. The DB
    // CHECK (partners_enabled_products_valid) refuses cardinality 0 anyway, so
    // the only thing an empty state could produce is a save that fails; better
    // that the checkbox simply does not come off than that it comes off and
    // then the save is refused for a reason the admin has to decode.
    if (next.length === 0) return current;
    return {
      enabled_products: next,
      // Clear a default whose product just went away. Leaving it would send
      // {enabled:['rentals'], default:'transfers'} — a shape the admin cannot
      // see is wrong, because both fields individually look like what they
      // asked for; it is only the pair that is illegal.
      default_product:
        current.default_product === action.toggle ? null : current.default_product,
    };
  }

  return {
    enabled_products: [...enabled, action.toggle],
    // Checking a product never disturbs a default: widening what is on offer
    // says nothing about which one the SDK should open on.
    default_product: current.default_product,
  };
}

/** The options the default-product `<select>` may offer — the enabled products
 *  only, since a default outside that list is exactly what this module exists
 *  to prevent. Filtered through isPartnerProduct so a stray value on a row
 *  (a widget name, a product we later removed) cannot become a selectable
 *  option. */
export function defaultProductOptions(state: ProductState): PartnerProduct[] {
  return state.enabled_products.filter(isPartnerProduct);
}
